import React from "react";
import { InputBasic, SelectBasic } from "components/common/form";
import { SelectAutocomplete as SelectAutocompleteBasic } from "components/common/form/select/select-autocomplete";
import { CheckboxWithLabel } from "components/haven-design/form/checkbox";

import {
  QueryInterestFormData,
  QueryInterestFormData_applicationFormMetadata_schema_options as SchemaOptionType,
} from "types/gql-types";
import { Column } from "components/common/html";

type ValidationSchema = NonNullable<QueryInterestFormData["applicationFormMetadata"]>["schema"]

export const checkIrregularField = (fieldName: string) => {
  if (fieldName.endsWith("address_1")) {
    return fieldName.replace("address_1", "address1");
  }
  if (fieldName.endsWith("address_2")) {
    return fieldName.replace("address_2", "address2");
  }
  return fieldName;
};

export const toSnakeCase = (fieldName: string) => fieldName.replace(/(?:[A-Z]|\d+)/g, "_$&").toLowerCase();
// determine if a field name is present in form schema
// isFieldInSchema(string, list(map)):: boolean
export const isFieldInSchema = (fieldName: string, schema = [] as ValidationSchema):boolean => {
  // For household member fields, use index 1 for validation when index > 1
  const normalizedFieldName = fieldName.replace(
    /householdMember(?:\d+)/,
    (_match, num) => `householdMember${parseInt(num, 10) > 1 ? 1 : num}`,
  );
  const snakeCaseFieldName = checkIrregularField(toSnakeCase(normalizedFieldName));
  return (schema ? (
    schema
      .map((schemaItem: NonNullable<ValidationSchema>[number]) =>
        schemaItem?.name === snakeCaseFieldName)
      .includes(true)) : false);
};

export const getLabelFromSchema = (schema: ValidationSchema, inputName: string): string => {
  const snakeCaseName = toSnakeCase(inputName);
  if (!schema) return "No label found";
  const item = schema.filter(x => x && x.name === snakeCaseName)[0] || { label: "No label found" };
  return item.label || "No label found";
};

// little helper component to autohide fields not used
// for a specific property
// {name: string, vs: ValidationSchema}
export const ShouldRenderField: React.FC<any> = (props) => {
  if (props.children && isFieldInSchema(props.name, props.vs)) {
    return props.children;
  }
  return null;
};

const getHouseholdCount = (formData: Record<string, any>): number => {
  const HOUSEHOLD_COUNT_LIMIT = 5;
  const count = Number(formData.householdCount || 0);

  return count >= HOUSEHOLD_COUNT_LIMIT ? HOUSEHOLD_COUNT_LIMIT : count;
};

// create a list of maps with each member name
export const gatherAllHouseholdMembers = (formData: Record<string, any>): {name: string}[] =>
  Array(Number(getHouseholdCount(formData))).fill(null).map((_data, i) => ({
    name: `${formData[`householdMember${i}FirstName`] || `${i + 1}`} ${
      formData[`householdMember${i}LastName`] || ""
    }`,
  }));

// wrapper components to reduce boilerplate

export const Input: React.FC<any> = (props) =>
  <InputBasic {...props} defaultValue={props.formData[props.name]} />;

export const Checkbox: React.FC<any> = ({ inline, formData, ...props }) => (
  <div style={{ display: inline ? "inline-block" : "block" }}>
    <CheckboxWithLabel
      defaultChecked={formData[props.name]}
      {...props}
      style={{ marginLeft: -5 }}
    />
  </div>
);

export const Select: React.FC<any> = props => (
  <Column width={props.width}>
    <SelectBasic defaultValue={props.formData[props.name]} {...props} />
  </Column>
);

export const SelectAutocomplete: React.FC<any> = props => (
  <Column width={props.width}>
    <SelectAutocompleteBasic defaultValue={props.formData[props.name]} fullWidth {...props} />
  </Column>
);

export const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const getUnitSizeOptions = (schema: ValidationSchema): SchemaOptionType[] => {
  if (!schema) return [];
  const unitSize = schema.find((question) => question && question.name === "unit_size");
  const options = (unitSize && unitSize.options) || [];
  return options.map((option) => ({
    ...option,
  }));
};
